import { z } from "zod";

// Type-only for the same reason as the roster module: the console parses its
// cluster read through this file, and it must not drag the generated enum table
// into the browser bundle.
import type { DbEnum } from "../../../generated/prisma-enums";
import { enumReadSchema } from "../../core/enum-read.schema";
import {
  faultedVibeClusterComponents,
  VIBE_CLUSTER_COMPONENT_CUSTOMER_COPY,
  type VibeClusterComponent,
  type VibeClusterComponentStatuses
} from "./tenant-cluster-components";
import {
  resolveVibeTenantClusterRosterState,
  type VibeTenantClusterRosterState
} from "./tenant-cluster-roster-state";

/**
 * What a TENANT is told about the condition of its own dedicated cluster.
 *
 * Read from TWO facts, ranked: the lifecycle status through the operator
 * roster's resolver, and every component the agent reports
 * (`./tenant-cluster-components.ts`). The reconcile loop writes `DEGRADED` on any
 * non-converged preview, and a deploy whose program changes what every tenant's
 * stacks would apply drifts the whole fleet into it — so a customer surface
 * reading `status` alone warns every tenant after such a deploy. And a surface reading
 * the roster alone tells a tenant whose git host answers 502 to every push that a
 * routine update is pending and there is nothing to do, which it did, for 15
 * hours, while every deploy was refused.
 *
 *   - `FAULT` — something is wrong. The roster states that page an operator
 *     (`cluster-degraded` detector): `DEGRADED`, and the `DEGRADED_UNCLASSIFIED`
 *     row that claims degraded with no evidence — OR any component of a serving
 *     cluster reporting a fault, which pages through that component's own
 *     detector. The loud default is kept on purpose: the roster only ever
 *     SUBTRACTS a warning, on a whole reading of the reason as routine.
 *   - `UPDATE_PENDING` — serving, every component working, and the only thing
 *     between it and converged is a set of routine pending changes.
 *   - `NOMINAL` — neither. The lifecycle `status` beside it says the rest
 *     (provisioning, retired, …); this axis claims nothing about it.
 *
 * A higher kind always wins (`VIBE_CLUSTER_CONDITION_KIND_RANK`), so no
 * combination of facts can land a faulted component under a sentence written
 * for a routine update.
 *
 * Carries a customer sentence rather than the raw `statusReason`: the reason is
 * Pulumi resource names and provider bodies, operator material that never
 * crosses the tenant boundary. The pause REASON stays operator-side for the same
 * reason; only the fact of the pause crosses, because it changes when a repair
 * can happen.
 */
export const VIBE_CLUSTER_CONDITION_KIND_VALUES = ["NOMINAL", "UPDATE_PENDING", "FAULT"] as const;

export type VibeClusterConditionKind = (typeof VIBE_CLUSTER_CONDITION_KIND_VALUES)[number];

export const VibeClusterConditionKindSchema = z.enum(VIBE_CLUSTER_CONDITION_KIND_VALUES);

/** `VibeClusterConditionKindSchema` as a reader parses it: a value this build does not list reads as unlisted. */
export const VibeClusterConditionKindReadSchema = enumReadSchema(
  VIBE_CLUSTER_CONDITION_KIND_VALUES
);

export const VibeClusterConditionSchema = z.object({
  kind: VibeClusterConditionKindReadSchema,
  /** One plain-language sentence for the kind, written for the customer. */
  summary: z.string()
});

export type VibeClusterCondition = z.infer<typeof VibeClusterConditionSchema>;

/**
 * Exhaustive over the roster's states, so a new roster state is a compile error
 * here rather than a tenant silently inheriting some other state's answer.
 */
const CONDITION_KIND_BY_ROSTER_STATE: Record<
  VibeTenantClusterRosterState,
  VibeClusterConditionKind
> = {
  PROVISIONING: "NOMINAL",
  HEALTHY: "NOMINAL",
  UPDATING: "NOMINAL",
  PENDING_CONFIG_DIFF: "UPDATE_PENDING",
  DEGRADED: "FAULT",
  DEGRADED_UNCLASSIFIED: "FAULT",
  DISABLING: "NOMINAL",
  DISABLED_RETAINED: "NOMINAL",
  DESTROYING: "NOMINAL",
  DESTROYED: "NOMINAL"
};

/**
 * Which kind wins when the lifecycle and the components disagree: the higher.
 * A fault outranks a pending update, and a pending update outranks nominal, so
 * the sentence a tenant reads is always written for the worst thing true of it.
 */
export const VIBE_CLUSTER_CONDITION_KIND_RANK: Readonly<Record<VibeClusterConditionKind, number>> =
  {
    NOMINAL: 0,
    UPDATE_PENDING: 1,
    FAULT: 2
  };

type VibeTenantClusterStatusLiteral = (typeof DbEnum.VIBE_TENANT_CLUSTER_STATUS_VALUES)[number];

/**
 * Whether a component's reported status describes something RUNNING in this
 * lifecycle state. Only a serving cluster has live components: on provisioning
 * the cluster's own status is the honest answer, and on disabling or retired
 * states the columns are leftovers from a prior life — nothing nulls them on
 * teardown. Exhaustive, so a new lifecycle status is classified before it ships.
 */
const COMPONENTS_ARE_LIVE: Readonly<Record<VibeTenantClusterStatusLiteral, boolean>> = {
  PROVISIONING: false,
  HEALTHY: true,
  UPDATING: true,
  DEGRADED: true,
  DISABLING: false,
  DISABLED_RETAINED: false,
  DESTROYING: false,
  DESTROYED: false
};

/**
 * The sentence a tenant reads for each kind when nothing more specific is true —
 * no component faulted and the cluster not held out of the reconcile sweep. Each
 * claim is one the platform keeps: a fault pages an operator (after the alert
 * policy's `degradedForMinutes`), and a routine pending diff is applied by the
 * reconcile loop with the cluster serving throughout.
 */
export const VIBE_CLUSTER_CONDITION_SUMMARY: Record<VibeClusterConditionKind, string> = {
  NOMINAL: "No faults and no pending changes.",
  UPDATE_PENDING:
    "A routine configuration update is waiting to be applied. It applies automatically and your apps keep serving — there is nothing you need to do.",
  FAULT:
    "Part of your cluster is not in its expected state. Our team is alerted automatically and will repair it; some apps may be affected until then."
};

/**
 * What a tenant is told about a pause: that automatic work on its cluster is
 * held by the operator, so nothing applies or repairs until it is released. The
 * pause's reason is operator material and never crosses.
 */
const HELD = "Automatic updates and repairs on your cluster are paused by our team";

/** The input the resolver reads: the cluster row's lifecycle, its components, and its pause. */
export interface VibeClusterConditionInput extends VibeClusterComponentStatuses {
  readonly status: VibeTenantClusterStatusLiteral;
  readonly statusReason: string | null;
  /**
   * Whether an operator holds this cluster out of the reconcile sweep. Null
   * where the reader cannot know — an older backend that never sent it — and
   * then the sentence makes no claim about when anything applies or is repaired.
   */
  readonly reconcilePaused: boolean | null;
}

/** Resolve the customer-safe condition of one cluster row. Pure and total. */
export function resolveVibeClusterCondition(
  input: VibeClusterConditionInput
): VibeClusterCondition {
  const lifecycle = CONDITION_KIND_BY_ROSTER_STATE[resolveVibeTenantClusterRosterState(input)];
  const faulted = COMPONENTS_ARE_LIVE[input.status] ? faultedVibeClusterComponents(input) : [];
  const fromComponents: VibeClusterConditionKind = faulted.length > 0 ? "FAULT" : "NOMINAL";
  const kind =
    VIBE_CLUSTER_CONDITION_KIND_RANK[fromComponents] > VIBE_CLUSTER_CONDITION_KIND_RANK[lifecycle]
      ? fromComponents
      : lifecycle;
  return {
    kind,
    summary: summaryOf(kind, { lifecycle, faulted, paused: input.reconcilePaused })
  };
}

function summaryOf(
  kind: VibeClusterConditionKind,
  facts: {
    readonly lifecycle: VibeClusterConditionKind;
    readonly faulted: readonly VibeClusterComponent[];
    readonly paused: boolean | null;
  }
): string {
  switch (kind) {
    case "NOMINAL":
      return VIBE_CLUSTER_CONDITION_SUMMARY.NOMINAL;
    case "UPDATE_PENDING":
      return updatePendingSummary(facts.paused);
    case "FAULT":
      return faultSummary(facts);
  }
}

/**
 * A routine update applies on its own only while the reconcile loop is allowed
 * to reach the cluster, so "it applies automatically" is said only when that is
 * known to be true.
 */
function updatePendingSummary(paused: boolean | null): string {
  if (paused === false) return VIBE_CLUSTER_CONDITION_SUMMARY.UPDATE_PENDING;
  const when =
    paused === true
      ? `${HELD}, so it applies once they resume them.`
      : "Your apps keep serving in the meantime.";
  return `A routine configuration update is waiting to be applied. ${when} There is nothing you need to do.`;
}

/**
 * A fault names every faulted component and what it being down takes away,
 * then says who is told and when it can be repaired. It never says the apps
 * keep serving and never says there is nothing to do: a faulted component
 * refuses something the tenant relies on.
 */
function faultSummary(facts: {
  readonly lifecycle: VibeClusterConditionKind;
  readonly faulted: readonly VibeClusterComponent[];
  readonly paused: boolean | null;
}): string {
  const components = facts.faulted.map(componentLine);
  const lifecycle =
    facts.lifecycle === "FAULT"
      ? [
          components.length > 0
            ? "Other parts of your cluster are not in their expected state either."
            : "Part of your cluster is not in its expected state."
        ]
      : [];
  const repair =
    facts.paused === true
      ? `Our team is alerted automatically. ${HELD}, so the repair waits until they resume them.`
      : facts.paused === false
        ? "Our team is alerted automatically and will repair it; some apps may be affected until then."
        : "Our team is alerted automatically; some apps may be affected until it is repaired.";
  return [...components, ...lifecycle, repair].join(" ");
}

/** One faulted component, said to the tenant: what it is, and what it refuses. */
function componentLine(component: VibeClusterComponent): string {
  const { name, blocked } = VIBE_CLUSTER_COMPONENT_CUSTOMER_COPY[component];
  return `${name} is not working: ${blocked}.`;
}
